import { type ComponentType } from 'react'
import { type RPPDomain } from '@retailer-platform/dashboard/core'
import {
  AdminNavEntryPointV2,
  AdminNavEntryPointV3,
  NavEntryPoint,
  NavEntryPointV3,
} from '@retailer-platform/dashboard/routing'
import { insightsPortalRouter } from './routing/insightsPortalRouter'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import { insightsPortalAccessControl } from './access-control/insightsPortalAccess.configuration'
import { insightsPortalSummaryReportAccessControl } from './access-control/insightsPortalSummaryReportAccess.configuration'
import { insightsPortalAdsDashboardAccessControl } from './access-control/insightsPortalAdsDashboardAccess.configuration'
import { insightsPortalCampaignInsightsAccessControl } from './access-control/insightsPortalCampaignInsights.configuration'
import { insightsPortalDataExplorerAccessControl } from './access-control/insightsPortalDataExplorerAccess.configuration'
import { insightsPortalCollectionsAccessControl } from './access-control/insightsPortalCollectionInsights.configuration'
import { insightsPortalCreativeInsightsAccessControl } from './access-control/insightsPortalCreativeInsights.configuration'
import { insightsPortalDashboardBuilderControl } from './access-control/insightsPortalDashboardBuilderAccess.configuration'
import { InsightsPortalStoreLaborPerformanceAccessControl } from './access-control/insightsPortalStoreLaborPerformanceAccess.configuration'
import { insightsPortalDeliveryDashboardAccessControl } from './access-control/insightsPortalDeliveryDashboardAccess.configuration'
import { insightsPortalAwarenessCampaignInsightsAccessControl } from './access-control/insightsPortalAwarenessCampaignInsights.configuration'
import { insightsPortalFlyersAccessControl } from './access-control/insightsPortalFlyersAccess.configuration'
import { insightsPortalCaperDashboardAccessControl } from './access-control/insightsPortalCaperDashboardAccess.configuration'
import { insightsPortalCartAssistantDashboardAccessControl } from './access-control/insightsPortalCartAssistantDashboardAccess.configuration'
import { insightsPortalOfferIngestionAccessControl } from './access-control/insightsPortalOfferIngestionAccess.configuration'
import { insightsPortalDeliveryOrchestrationPerformanceAccessControl } from './access-control/insightsPortalDeliveryOrchestrationPerformanceAccess.configuration'
import { InsightsPortalWarehouseContextFilterNavWrapper } from './routing/InsightsPortalWarehouseContextFilterNavWrapper'
import { insightsAdminRouter } from './routing/insightsAdminRouter'
import { Permission } from './access-control/permissions'

// Cast to ComponentType<any> to satisfy strict function types for NavItemWrapper
const NavWrapper = InsightsPortalWarehouseContextFilterNavWrapper as ComponentType<any>

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'insights-portal',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'insights-portal-dashboard-tool',
  },
  routes,
  translations: { EN_US },
  lazyTranslations: {
    ES_ES: () => import('./translations/es-ES.json'),
    FR_FR: () => import('./translations/fr-FR.json'),
  },
  routers: {
    scoped: [insightsPortalRouter],
    admin: [insightsAdminRouter],
  },
  navigationEntries: [
    {
      attachToV2: NavEntryPoint.AnalyticsStorefrontPerformance,
      attachToV3: NavEntryPointV3.Performance,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'View a summary dashboard of top sales, order fulfillment, and customer engagement metrics across your stores',
          labelId: 'insightsPortalDomain.summaryReport.nav',
          route: 'insights-portal-executive-summary',
          accessControl: insightsPortalSummaryReportAccessControl,
          NavItemWrapper: NavWrapper,
          position: 0,
          positionNavV2: 0,
          positionNavV3: 0,
          inToolPermissions: [
            {
              slug: Permission.InsightsPortalCampaign,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.InsightsPortalCampaign,
            },
          ],
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsStorefrontPerformance,
      attachToV3: NavEntryPointV3.Performance,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'View store order fulfillment, processing time, and completion rate analytics',
          labelId: 'insightsPortalDomain.storePerformance.completedOrders.title',
          labelIdV3: 'insightsPortalDomain.storePerformance.completedOrders.title.v3',
          route: 'insights-portal-store-performance-completed-orders',
          accessControl: insightsPortalAccessControl,
          NavItemWrapper: NavWrapper,
          position: 1,
          positionNavV2: 1,
          positionNavV3: 1,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsStorefrontPerformance,
      attachToV3: NavEntryPointV3.Performance,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'View item-level customer shopping behavior and basket composition analytics',
          labelId: 'insightsPortalDomain.storePerformance.basket.title',
          route: 'insights-portal-store-performance-basket-overview',
          accessControl: insightsPortalAccessControl,
          NavItemWrapper: NavWrapper,
          position: 2,
          positionNavV2: 2,
          positionNavV3: 2,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsStorefrontPerformance,
      attachToV3: NavEntryPointV3.Performance,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'View search analytics and customer search behavior patterns',
          labelId: 'insightsPortalDomain.storePerformance.search.title',
          route: 'insights-portal-store-performance-search',
          accessControl: insightsPortalAccessControl,
          NavItemWrapper: NavWrapper,
          position: 3,
          positionNavV2: 3,
          positionNavV3: 4,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsStorefrontPerformance,
      attachToV3: NavEntryPointV3.Performance,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'View product image coverage and completeness metrics',
          labelId: 'insightsPortalDomain.searchDashboard.metrics.imageCoverage',
          route: 'insights-portal-store-performance-image-coverage',
          accessControl: insightsPortalAccessControl,
          NavItemWrapper: NavWrapper,
          position: 5,
          positionNavV2: 5,
          positionNavV3: 3,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsReportsAndLogs,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'View offer ingestion health including parse success, rejected offers, and error codes by retailer',
          labelId: 'insightsPortalDomain.offerIngestionDashboard.nav',
          route: 'insights-portal-offer-ingestion',
          accessControl: insightsPortalOfferIngestionAccessControl,
          NavItemWrapper: NavWrapper,
          position: 5,
          positionNavV2: 3,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsStorefrontPerformance,
      attachToV3: NavEntryPointV3.Performance,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'View Cart Assistant analytics',
          labelId: 'insightsPortalDomain.cartAssistantDashboard.nav',
          route: 'insights-portal-cart-assistant-dashboard',
          accessControl: insightsPortalCartAssistantDashboardAccessControl,
          NavItemWrapper: NavWrapper,
          position: 4,
          positionNavV2: 4,
          positionNavV3: 5,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsStorefrontPerformance,
      attachToV3: NavEntryPointV3.Performance,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'View Caper smart cart analytics including sessions, orders, loyalty, and store performance metrics',
          labelId: 'insightsPortalDomain.caperReportingDashboard.title',
          labelIdV3: 'insightsPortalDomain.caperReportingDashboard.title.v3',
          route: 'insights-portal-caper-reporting-dashboard-overview',
          accessControl: insightsPortalCaperDashboardAccessControl,
          NavItemWrapper: NavWrapper,
          position: 6,
          positionNavV2: 6,
          positionNavV3: 7,
          subRoutes: [
            'insights-portal-caper-reporting-dashboard-sessions',
            'insights-portal-caper-reporting-dashboard-session-trends',
            'insights-portal-caper-reporting-dashboard-store-trend',
            'insights-portal-caper-reporting-dashboard-audits',
            'insights-portal-caper-reporting-dashboard-loyalty',
            'insights-portal-caper-reporting-dashboard-operations',
            'insights-portal-caper-reporting-dashboard-store-performance',
            'insights-portal-caper-reporting-dashboard-session-details',
          ],
        },
      ],
    },

    {
      attachToV2: NavEntryPoint.OperationsLabor,
      attachToV3: NavEntryPointV3.Fulfillment,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'View labor productivity, staffing levels, and workforce scheduling performance across stores',
          labelId: 'insightsPortalDomain.storeLaborPerformanceV2.title',
          route: 'insights-portal-store-labor-performance-v2',
          accessControl: InsightsPortalStoreLaborPerformanceAccessControl,
          NavItemWrapper: NavWrapper,
          position: 1,
          positionNavV2: 1,
          positionNavV3: 0,
          inToolPermissions: [
            {
              slug: Permission.Analytics,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.Analytics,
            },
            {
              slug: Permission.PickupAnalytics,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.PickupAnalytics,
            },
          ],
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.OperationsLabor,
      attachToV3: NavEntryPointV3.Fulfillment,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'View delivery performance metrics including order completion, accuracy, and driver performance',
          labelId: 'insightsPortalDomain.deliveryPerformance.title',
          route: 'insights-portal-delivery-performance-overview',
          accessControl: insightsPortalDeliveryDashboardAccessControl,
          NavItemWrapper: NavWrapper,
          position: 0,
          positionNavV2: 0,
          positionNavV3: 6,
          subRoutes: ['insights-portal-delivery-performance-delivery'],
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.OperationsLabor,
      attachToV3: NavEntryPointV3.Fulfillment,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'View delivery orchestration performance metrics and analytics',
          labelId: 'insightsPortalDomain.deliveryOrchestrationPerformance.title',
          route: 'insights-portal-delivery-orchestration-performance',
          accessControl: insightsPortalDeliveryOrchestrationPerformanceAccessControl,
          NavItemWrapper: NavWrapper,
          position: 0,
          positionNavV2: 0,
          positionNavV3: 7,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsMarketing,
      attachToV3: NavEntryPointV3.Performance,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'View advertising performance metrics across sponsored products, display ads, and promotions',
          labelId: 'insightsPortalDomain.adsDashboard.nav',
          labelIdV3: 'insightsPortalDomain.adsDashboard.nav.v3',
          route: 'insights-portal-overview-ads-dashboard',
          accessControl: insightsPortalAdsDashboardAccessControl,
          NavItemWrapper: NavWrapper,
          position: 5,
          positionNavV2: 3,
          positionNavV3: 6,
          subRoutes: [
            'insights-portal-sponsored-product-ads-dashboard',
            'insights-portal-display-ads-dashboard',
            'insights-portal-promotions-ads-dashboard',
          ],
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsMarketing,
      attachToV3: NavEntryPointV3.Performance,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'View marketing campaign performance and ROI analytics',
          labelId: 'insightsPortalDomain.campaignsAnalytics.nav',
          route: 'insights-portal-marketing-campaigns-analytics-overview',
          accessControl: insightsPortalCampaignInsightsAccessControl,
          NavItemWrapper: NavWrapper,
          position: 7,
          positionNavV2: 0,
          positionNavV3: 9,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsMerchandising,
      attachToV3: NavEntryPointV3.Performance,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'View collection performance and merchandising analytics',
          labelId: 'insightsPortalDomain.collections.nav',
          route: 'insights-portal-merchandising-collections-overview',
          accessControl: insightsPortalCollectionsAccessControl,
          NavItemWrapper: NavWrapper,
          position: 9,
          positionNavV3: 8,
          inToolPermissions: [],
          dividerBefore: true,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsMarketing,
      attachToV3: NavEntryPointV3.Performance,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'View creative placements and asset performance analytics',
          labelId: 'insightsPortalDomain.creatives.nav',
          route: 'insights-portal-placements-insights',
          accessControl: insightsPortalCreativeInsightsAccessControl,
          NavItemWrapper: NavWrapper,
          position: 10,
          positionNavV2: 1,
          positionNavV3: 11,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsMarketing,
      attachToV3: NavEntryPointV3.Performance,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'View awareness campaign performance and brand metrics',
          labelId: 'insightsPortalDomain.awarenessCampaigns.nav',
          route: 'insights-portal-marketing-awareness-campaigns-analytics-overview',
          accessControl: insightsPortalAwarenessCampaignInsightsAccessControl,
          NavItemWrapper: NavWrapper,
          position: 8,
          positionNavV2: 2,
          positionNavV3: 10,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsReportsAndLogs,
      attachToV3: NavEntryPointV3.Reports,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'Create custom reports, explore detailed analytics data, and export analysis results',
          labelId: 'insightsPortalDomain.dataExplorer.navBeta',
          route: 'insights-portal-reports-logs-custom-report-list',
          accessControl: insightsPortalDataExplorerAccessControl,
          positionNavV2: 1,
          positionNavV3: 1,
          subRoutes: [
            'insights-portal-reports-logs-custom-report-exports-list',
            'insights-portal-reports-logs-custom-report-preview',
          ],
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.AnalyticsMarketing,
      attachToV3: NavEntryPointV3.Performance,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'View flyer performance',
          labelId: 'insightsPortalDomain.flyers.nav',
          route: 'insights-portal-marketing-flyers-overview',
          accessControl: insightsPortalFlyersAccessControl,
          NavItemWrapper: NavWrapper,
          positionNavV2: 4,
          positionNavV3: 12,
          subRoutes: ['insights-portal-marketing-flyers-single-flyer'],
        },
      ],
    },
  ],
  adminNavigationEntries: [
    {
      attachToV2: AdminNavEntryPointV2.Analytics,
      attachToV3: AdminNavEntryPointV3.Performance,
      subNavItems: [
        {
          route: 'insights-portal-admin-dashboard-create',
          labelId: 'insightsPortalDomain.page.dashboardBuilder.nav',
          accessControl: insightsPortalDashboardBuilderControl,
        },
      ],
    },
  ],
}

export default definition
